import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { getCurrentUserId, getNhDealApplicationId, getCurrentAccessToken } from "features/authSlice";
import { useGetDashboardItemsQuery } from "pages/dashboard/home/dashboardService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { useLoanOfficer } from "hooks/useLoanOfficer";
import {
    useGetSurveyDetailsQuery,
    useCreateSurveyMutation,
    RatingSelection,
    Survey,
    SurveyType,
    CreateSurveyDto,
    TriggerPoint,
    useUpdateSurveyMutation,
    useUpdateSurveyDisplayedCountMutation,
} from "../surveyService";
import CtcPopup from "pages/dashboard-popup/ctc-popup/CtcPopup";
import NpsRatingModal from "./nps-rating/NpsRatingModal";
import {
    CTC_POPUP_DURATION,
    LOAN_STATUS,
    NPS_LAST_SESSION,
    NPS_RETRY_LIMIT,
} from "utilities/Constant";

export default function NpsSurvey() {
    const [showCTCPopup, setShowCTCPopup] = useState(false);
    const [showNpsPopup, setShowNpsPopup] = useState(false);
    const [createdSurvey, setCreatedSurvey] = useState<Survey | null>(null);
    const hasSurveyBeenCreated = useRef(false);
    const [surveySubmitted, setSurveySubmitted] = useState(false);

    const userId = useSelector(getCurrentUserId);
    const nhDealApplicationId = useSelector(getNhDealApplicationId);
    const accessToken = useSelector(getCurrentAccessToken);

    const { data: dashboardData } = useGetDashboardItemsQuery(
        { dealApplicationId: nhDealApplicationId, userId },
        { skip: !userId || !nhDealApplicationId }
    );

    const { data: featureFlags } = useGetFeatureFlagsQuery();

    const loanOfficerDetails = useLoanOfficer();

    const {
        data: surveyDetails,
        isSuccess: isSurveyDetailsSuccess,
    } = useGetSurveyDetailsQuery(
        { userId, nhDealApplicationId, surveyType: SurveyType.NPS },
        { skip: !userId || !nhDealApplicationId }
    );

    const [createSurvey] = useCreateSurveyMutation();
    const [updateSurvey] = useUpdateSurveyMutation();
    const [updateSurveyDisplayedCount] = useUpdateSurveyDisplayedCountMutation();

    /** Check if NPS should be shown */
    const shouldShowNps = useCallback((): boolean => {
        const isLoanClosed = dashboardData?.loanStatus?.status === LOAN_STATUS.CLOSE;
        const lastSession = localStorage.getItem(NPS_LAST_SESSION);
        const currentAccessToken = accessToken;
        if (!isLoanClosed || surveyDetails === undefined || lastSession === currentAccessToken) return false;

        // Check channel-specific feature flags
        const firstDomainName = (loanOfficerDetails.loanOfficer?.domains?.[0]?.domainName ?? "").trim().toLowerCase();
        const isCdChannel = firstDomainName === "cd";
        const isRetailChannel = firstDomainName === "retail";

        // Determine if NPS is enabled for this channel
        const isNpsEnabledForChannel =
            (isCdChannel && (featureFlags?.npsSurveyCdEnabled ?? false)) ||
            (isRetailChannel && (featureFlags?.npsSurveyRetailEnabled ?? false)) ||
            (!isCdChannel && !isRetailChannel && (featureFlags?.npsSurveyEnabled ?? false));

        return (
            isLoanClosed &&
            isSurveyDetailsSuccess &&
            isNpsEnabledForChannel &&
            (!surveyDetails || (surveyDetails.displayedCount < NPS_RETRY_LIMIT && !surveyDetails.completedDate))
        );
    }, [dashboardData, surveyDetails, isSurveyDetailsSuccess, featureFlags, loanOfficerDetails]);


    const triggerCreateSurvey = useCallback(() => {
        if (
            userId &&
            nhDealApplicationId &&
            isSurveyDetailsSuccess &&
            !hasSurveyBeenCreated.current &&
            !surveyDetails
        ) {
            hasSurveyBeenCreated.current = true;
            const surveyData: CreateSurveyDto = {
                type: SurveyType.NPS,
                triggerPoint: TriggerPoint.ClearToClose,
                Device: navigator.userAgent || "Unknown",
            };

            createSurvey({ userId, nhDealApplicationId, surveyData })
                .unwrap()
                .then((result) => {
                    setCreatedSurvey(result);
                })
                .catch((error) => {
                    console.error("Failed to create survey:", error);
                    hasSurveyBeenCreated.current = false;
                });
        }
    }, [
        userId,
        nhDealApplicationId,
        isSurveyDetailsSuccess,
        surveyDetails,
        createSurvey,
    ]);

    const triggerUpdateSurvey = useCallback(async (data: RatingSelection | null) => {
        const currentSurvey = surveyDetails || createdSurvey;
        if (!currentSurvey || !userId || !nhDealApplicationId) {
            console.error("No survey found to update", { surveyDetails, createdSurvey });
            return;
        }
        const updatedSurvey: Survey = {
            ...currentSurvey,
            score: data ? data?.rating : undefined,
            completedDate: data ? new Date() : undefined,
            updatedOn: new Date(),
            displayedCount: (currentSurvey.displayedCount || 0) + 1,
            feedbacks: data ? [{ question: data?.ratingDescription, answer: data?.comment }] : []
        };

        if (currentSurvey.id)
            await updateSurvey({ surveyId: currentSurvey.id, surveyData: updatedSurvey });
    }, [
        userId,
        nhDealApplicationId,
        isSurveyDetailsSuccess,
        surveyDetails,
        createSurvey,
    ]);

    const triggerUpdateSurveyDisplayedCount = async () => {
        const currentSurvey = surveyDetails || createdSurvey;
        if (!currentSurvey?.id) return;
        await updateSurveyDisplayedCount({ surveyId: currentSurvey.id });
    }

    const handleCloseNpsModal = useCallback(() => {
        setShowNpsPopup(false);
    }, []);

    const handleDismissNpsModal = () => {
        if (!surveySubmitted) void triggerUpdateSurveyDisplayedCount();
        setShowNpsPopup(false);
        localStorage.setItem(NPS_LAST_SESSION, accessToken);
    };

    const handleSubmitSurvey = (data: RatingSelection | null) => {
        setSurveySubmitted(true);
        void triggerUpdateSurvey(data);
        localStorage.setItem(NPS_LAST_SESSION, accessToken);
    };

    useEffect(() => {
        if (shouldShowNps()) {
            triggerCreateSurvey();
            setShowCTCPopup(true);

            const timer = setTimeout(() => {
                setShowCTCPopup(false);
                setShowNpsPopup(true);
            }, CTC_POPUP_DURATION);

            return () => clearTimeout(timer);
        }
    }, [shouldShowNps, triggerCreateSurvey]);

    return (
        <>
            {showCTCPopup && <CtcPopup />}
            {showNpsPopup && (
                <NpsRatingModal
                    onCloseModal={handleCloseNpsModal}
                    onDismissModal={handleDismissNpsModal}
                    onSubmit={handleSubmitSurvey}
                />
            )}
        </>
    );
}
